// DAS-2107 / DAS-2140 / DAS-2152: "Your Exposures" modal
// Tabbed view: Breakdown table + pie charts for region/currency/country/industry/sector/assettype.
// Concentration metrics panel: Top-N %, HHI index, Effective N (DAS-2152).

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Tooltip,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, Paper, TextField, InputAdornment, Chip,
  Tabs, Tab, CircularProgress, LinearProgress, Collapse,
  Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PieChart } from '@mui/x-charts/PieChart';
import api from '../../functions/api';
import getUserId from '../../functions/getUserId';
import ClickableTickerChip from './ClickableTickerChip';

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `€${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtPct = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `${Number(v).toFixed(1)}%`;
};

const PIE_COLORS = [
  '#00b8d4', '#ab47bc', '#e91e63', '#3949ab', '#2e7d32',
  '#f57c00', '#c62828', '#00695c', '#5c6bc0', '#8d6e63',
  '#ff6f00', '#1565c0', '#6a1b9a', '#43a047', '#d84315',
];
const MAX_PIE_SLICES = 8;
const OTHER_SLICE_COLOR = '#9e9e9e';

const EXPOSURE_TABS = [
  { key: 'breakdown', label: 'Breakdown' },
  { key: 'region', label: 'Region' },
  { key: 'currency', label: 'Currency' },
  { key: 'country', label: 'Country' },
  { key: 'industry', label: 'Industry' },
  { key: 'sector', label: 'Sector' },
  { key: 'assettype', label: 'Asset Type' },
];

const PIE_DIM_KEYS = {
  region: 'region',
  currency: 'base_currency',
  country: 'country',
  industry: 'industry',
  sector: 'sector',
  assettype: 'Assettype',
};

// DAS-2689: real estate and cash are not a geographic/market exposure, so they are
// excluded from the Region and Country charts (they stay on the Asset Type + Breakdown
// tabs). Keyed off the SAME strategic asset class the Asset-Type tab uses
// (classifyAssetType). Without this, EUR real estate (the owner's house + a rental,
// ~€1.55M) and EUR savings/deposits (~€0.6M) all fell into "Europe" and dominated it
// (~80%), hiding the true securities split (Europe ~47% / USA ~38%).
const NON_MARKET_ASSET_CLASSES = new Set(['Real Assets', 'Cash']);
const isNonMarketAssetClass = (assetClass) => NON_MARKET_ASSET_CLASSES.has(assetClass);
// Dimensions whose pie is derived from the holdings map (pie == drill-down by construction).
// Region/Country/Industry/Sector additionally exclude real estate & cash (see buildHoldingsMap).
const HOLDINGS_DERIVED_PIES = ['assettype', 'region', 'country', 'industry', 'sector'];
// Derived dims that are securities-only (real estate + cash excluded) → renormalised + "securities".
const SECURITIES_ONLY_DIMS = new Set(['region', 'country', 'industry', 'sector']);

// DAS-2689: the country column mixes ISO-2 codes and full names for the same place
// (US + United States, NL + Netherlands, GB + United Kingdom, CA + Canada, …), which
// split one country into two pie slices. Normalise codes to a single canonical name.
// Full names and fund markers ("Mix Eur"/"Mix"/"Mix Asia") pass through unchanged.
const COUNTRY_CODE_MAP = {
  US: 'United States', GB: 'United Kingdom', UK: 'United Kingdom', NL: 'Netherlands',
  DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain', CH: 'Switzerland',
  BE: 'Belgium', SE: 'Sweden', FI: 'Finland', NO: 'Norway', DK: 'Denmark',
  IE: 'Ireland', AT: 'Austria', PT: 'Portugal', LU: 'Luxembourg', PL: 'Poland',
  GR: 'Greece', CZ: 'Czech Republic', HU: 'Hungary', CA: 'Canada', JP: 'Japan',
  CN: 'China', HK: 'Hong Kong', TW: 'Taiwan', KR: 'South Korea', IN: 'India',
  SG: 'Singapore', AU: 'Australia', NZ: 'New Zealand', BR: 'Brazil', MX: 'Mexico',
  CL: 'Chile', CO: 'Colombia', IL: 'Israel', KY: 'Cayman Islands', ZA: 'South Africa',
};
const normalizeCountry = (c) => {
  const t = (c == null ? '' : String(c)).trim();
  if (!t) return 'Unknown';
  return COUNTRY_CODE_MAP[t.toUpperCase()] || t;
};

// DAS-2166: Map raw Yahoo Finance asset types to strategic asset classes
const ASSET_DIRECT_MAP = {
  Stock: 'Equity', STOCK: 'Equity', Equity: 'Equity', EQUITY: 'Equity',
  'Depositary Receipt': 'Equity', 'DEPOSITARY RECEIPT': 'Equity',
  ADR: 'Equity', GDR: 'Equity',
  Bond: 'Fixed Income', BOND: 'Fixed Income', 'Fixed Income': 'Fixed Income',
  'Corporate Bonds': 'Fixed Income',
  Cash: 'Cash', CASH: 'Cash', Savings: 'Cash', Deposits: 'Cash', 'Money Market': 'Cash',
  Cryptocurrency: 'Crypto', CRYPTOCURRENCY: 'Crypto', Crypto: 'Crypto',
  Commodities: 'Real Assets', 'Real Assets': 'Real Assets',
  Alternatives: 'Alternatives',
};
const _FI_RE = /bond|treasury|treasuries|fixed.?income|aggregate.?bond|govt|government|tips|inflation.?protect|investment.?grade|high.?yield|corporate.?bond|municipal|muni|credit|duration|coupon|debt|gilts|bund|obligatie|staatsobligatie|bedrijfsobligatie|\babs\b|asset.?backed/i;
const _REAL_RE = /real.?estate|reit|property|properties|commodity|commodities|gold|silver|platinum|palladium|oil|natural.?gas|energy.?commodity|timber|farmland|infrastructure|agriculture|metals|mining|copper|uranium|vastgoed|onroerend|woning|appartement|huurwoning|pand/i;
const _CRYPTO_RE = /crypto|bitcoin|btc|ethereum|eth|blockchain|digital.?asset|defi|web3/i;
const _ALT_RE = /hedge|long.?short|market.?neutral|merger.?arbitrage|managed.?futures|private.?equity|private.?credit|venture|mezzanine|distressed|multi.?strategy|absolute.?return|volatility|vix/i;
const _CASH_RE = /\bcash\b|money.?market|savings|deposit|liquidity|liquiditeiten|sweep/i;
const _FI_NAME_RE = /\b\d+(\.\d+)?%\s*(frn|perp|bond|note|debenture|coupon|float|fixed)\b|\b(frn|perpetual|perp)\b.*\b\d+(\.\d+)?%\b/i;

function classifyAssetType(assetType, companyName, sector) {
  const sectorLower = (sector || '').toLowerCase();
  if (sectorLower.includes('reit') || sectorLower.includes('real estate')) return 'Real Assets';
  const name = companyName || '';
  if (_FI_NAME_RE.test(name)) return 'Fixed Income';
  const at = (assetType || '').trim();
  if (ASSET_DIRECT_MAP[at]) return ASSET_DIRECT_MAP[at];
  const atUp = at.toUpperCase();
  if (['ETF', 'MUTUALFUND', 'ETN', 'EQUITY FUND', 'ETC'].includes(atUp)) {
    if (_FI_RE.test(name)) return 'Fixed Income';
    if (_REAL_RE.test(name)) return 'Real Assets';
    if (_CRYPTO_RE.test(name)) return 'Crypto';
    if (_ALT_RE.test(name)) return 'Alternatives';
    if (_CASH_RE.test(name)) return 'Cash';
    return 'Equity';
  }
  if (_CASH_RE.test(name)) return 'Cash';
  if (_FI_RE.test(name)) return 'Fixed Income';
  if (_REAL_RE.test(name)) return 'Real Assets';
  if (_CRYPTO_RE.test(name)) return 'Crypto';
  if (_ALT_RE.test(name)) return 'Alternatives';
  return 'Unclassified';
}

// ─── Concentration metrics (DAS-2152) ───────────────────────────

const HHI_LEVELS = [
  { max: 1500, label: 'Well diversified', color: '#2e7d32' },
  { max: 2500, label: 'Moderate concentration', color: '#f57c00' },
  { max: Infinity, label: 'Highly concentrated', color: '#c62828' },
];

function computeConcentration(exposures) {
  const values = exposures
    .map(e => parseFloat(e.total_amount_exposure) || 0)
    .filter(v => v > 0)
    .sort((a, b) => b - a);

  if (values.length === 0) return null;

  const total = values.reduce((s, v) => s + v, 0);
  const weights = values.map(v => v / total);

  const topN = (n) => {
    const slice = weights.slice(0, Math.min(n, weights.length));
    return slice.reduce((s, w) => s + w, 0) * 100;
  };

  const hhi = weights.reduce((s, w) => s + w * w, 0) * 10000;
  const effectiveN = hhi > 0 ? 10000 / hhi : values.length;
  const hhiLevel = HHI_LEVELS.find(l => hhi <= l.max) || HHI_LEVELS[HHI_LEVELS.length - 1];

  const topNames = exposures
    .map(e => ({
      ticker: e.ticker || '',
      company: e.company || '',
      val: parseFloat(e.total_amount_exposure) || 0,
    }))
    .filter(e => e.val > 0)
    .sort((a, b) => b.val - a.val);

  const topPositions = topNames.map(e => ({
    ticker: e.ticker,
    company: e.company,
    value: e.val,
    pct: (e.val / total) * 100,
  }));

  return {
    n: values.length,
    top1: { pct: topN(1), ticker: topNames[0]?.ticker || '' },
    top3: topN(3),
    top5: topN(5),
    top10: topN(10),
    hhi: Math.round(hhi),
    hhiLevel,
    effectiveN: effectiveN.toFixed(1),
    topPositions,
  };
}

const ConcentrationBar = ({ label, pct, tooltip, onClick }) => (
  <Tooltip title={tooltip} arrow placement="right">
    <Box
      sx={{ mb: 1, cursor: onClick ? 'pointer' : 'default', borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined }}
      onClick={onClick}
    >
      <Box display="flex" justifyContent="space-between" mb={0.25}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{pct.toFixed(1)}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        sx={{
          height: 6,
          borderRadius: 1,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': {
            bgcolor: pct > 60 ? '#c62828' : pct > 40 ? '#f57c00' : '#2e7d32',
            borderRadius: 1,
          },
        }}
      />
    </Box>
  </Tooltip>
);

const ConcentrationDetailDialog = ({ open, onClose, title, positions = [] }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '70vh' } }}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
      {title}
      <Chip label={`${positions.length} position${positions.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 0.5 }} />
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <TableContainer>
        <MuiTable size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Value (EUR)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Weight</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((p, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.ticker || '—'}</Typography></TableCell>
                <TableCell>{p.company || '—'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>{fmtCurrency(p.value)}</TableCell>
                <TableCell align="right">{fmtPct(p.pct)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </DialogContent>
  </Dialog>
);

const HHI_TOOLTIP = (
  <Box sx={{ maxWidth: 320 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
      Herfindahl-Hirschman Index (HHI)
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
      Measures portfolio concentration by summing the squared weights of all positions. A higher HHI means your portfolio is dominated by fewer holdings.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
      <b>Why it matters:</b> High concentration amplifies the impact of any single position moving against you. Monitoring HHI helps you decide when to rebalance.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block' }}>
      <b>Ranges:</b> &lt;1,500 = well diversified, 1,500–2,500 = moderate, &gt;2,500 = highly concentrated.
    </Typography>
  </Box>
);

const EFFECTIVE_N_TOOLTIP = (
  <Box sx={{ maxWidth: 340 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
      Effective Number of Positions
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
      Answers the question: <b>"How spread out is my money really?"</b> You may own many stocks, but if most of your money sits in just a few, you're less diversified than you think.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, p: 0.8, bgcolor: 'rgba(46,125,50,0.12)', borderRadius: 1 }}>
      <b>Good example:</b> You own 20 stocks, each around 5% of your portfolio. Effective positions ≈ 20 — your money is genuinely spread out.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, p: 0.8, bgcolor: 'rgba(198,40,40,0.12)', borderRadius: 1 }}>
      <b>Bad example:</b> You own 20 stocks, but 2 of them hold 40% each (80% total). The other 18 share just 20%. Effective positions ≈ 3 — despite owning 20 names, your portfolio's risk depends almost entirely on those 2 big positions.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block' }}>
      <b>Rule of thumb:</b> The closer this number is to your actual number of holdings, the more evenly your money is spread. A big gap means a few positions dominate your risk.
    </Typography>
  </Box>
);

const ConcentrationPanel = ({ metrics }) => {
  const [concDetail, setConcDetail] = useState(null);

  if (!metrics) return null;

  const handleBarClick = (n, label) => {
    const positions = (metrics.topPositions || []).slice(0, n);
    if (positions.length > 0) setConcDetail({ title: label, positions });
  };

  return (
    <>
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Paper variant="outlined" sx={{ flex: '1 1 220px', p: 2, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
            Position Concentration
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
            Click a bar to see the positions
          </Typography>
          <Box>
            <ConcentrationBar
              label={`Top 1 (${metrics.top1.ticker})`}
              pct={metrics.top1.pct}
              tooltip={`Largest position is ${metrics.top1.pct.toFixed(1)}% of portfolio — click to view`}
              onClick={() => handleBarClick(1, `Top 1 Position (${metrics.top1.ticker})`)}
            />
            {metrics.n >= 3 && (
              <ConcentrationBar label="Top 3" pct={metrics.top3} tooltip="Combined weight of 3 largest positions — click to view" onClick={() => handleBarClick(3, 'Top 3 Positions')} />
            )}
            {metrics.n >= 5 && (
              <ConcentrationBar label="Top 5" pct={metrics.top5} tooltip="Combined weight of 5 largest positions — click to view" onClick={() => handleBarClick(5, 'Top 5 Positions')} />
            )}
            {metrics.n >= 10 && (
              <ConcentrationBar label="Top 10" pct={metrics.top10} tooltip="Combined weight of 10 largest positions — click to view" onClick={() => handleBarClick(10, 'Top 10 Positions')} />
            )}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ flex: '1 1 180px', p: 2, minWidth: 180 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
              HHI Index
            </Typography>
            <Tooltip arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: 'rgba(33,33,33,0.97)', p: 1.5, maxWidth: 340 } } }} title={HHI_TOOLTIP}>
              <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="baseline" gap={1} mt={1}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: metrics.hhiLevel.color }}>
              {metrics.hhi.toLocaleString()}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: metrics.hhiLevel.color, mt: 0.25 }}>
            {metrics.hhiLevel.label}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            &lt;1,500 diversified · 1,500–2,500 moderate · &gt;2,500 concentrated
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ flex: '1 1 180px', p: 2, minWidth: 180 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
              Effective Positions
            </Typography>
            <Tooltip arrow placement="top" slotProps={{ tooltip: { sx: { bgcolor: 'rgba(33,33,33,0.97)', p: 1.5, maxWidth: 340 } } }} title={EFFECTIVE_N_TOOLTIP}>
              <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="baseline" gap={1} mt={1}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {metrics.effectiveN}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              of {metrics.n} actual
            </Typography>
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            Equivalent equal-weighted positions (1/HHI)
          </Typography>
        </Paper>
      </Box>

      <ConcentrationDetailDialog
        open={!!concDetail}
        onClose={() => setConcDetail(null)}
        title={concDetail?.title || ''}
        positions={concDetail?.positions || []}
      />
    </>
  );
};

// ─── Breakdown sub-tab ──────────────────────────────────────────

const ExposureBreakdownTable = ({ exposures, source }) => {
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('total_amount_exposure');
  const [order, setOrder] = useState('desc');

  const filtered = useMemo(() => {
    let rows = exposures;
    if (search) {
      const lower = search.toLowerCase();
      rows = rows.filter(r =>
        (r.ticker || '').toLowerCase().includes(lower) ||
        (r.company || '').toLowerCase().includes(lower)
      );
    }
    return [...rows].sort((a, b) => {
      const av = parseFloat(a[orderBy]) || 0;
      const bv = parseFloat(b[orderBy]) || 0;
      return order === 'asc' ? av - bv : bv - av;
    });
  }, [exposures, search, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('desc');
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <TextField
          size="small"
          placeholder="Search ticker or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} of {exposures.length} exposures
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '55vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'total_amount_exposure_perc'}
                  direction={orderBy === 'total_amount_exposure_perc' ? order : 'asc'}
                  onClick={() => handleSort('total_amount_exposure_perc')}
                >
                  Part of Total Portfolio
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'total_amount_exposure'}
                  direction={orderBy === 'total_amount_exposure' ? order : 'asc'}
                  onClick={() => handleSort('total_amount_exposure')}
                >
                  Total Amount Exposure
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'tracker_exposure_amount'}
                  direction={orderBy === 'tracker_exposure_amount' ? order : 'asc'}
                  onClick={() => handleSort('tracker_exposure_amount')}
                >
                  Tracker Exposure Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'stock_exposure'}
                  direction={orderBy === 'stock_exposure' ? order : 'asc'}
                  onClick={() => handleSort('stock_exposure')}
                >
                  Stock Exposure
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, idx) => {
              const isUnknown = row.part_underlying_assets_unknown != null;
              const trackerDetails = row.tracker_exposure
                ? row.tracker_exposure.split(', ')
                : [];

              return (
                <TableRow key={`${row.ticker}-${idx}`} hover>
                  <TableCell>
                    <Box>
                      <ClickableTickerChip ticker={row.ticker} source={source} />
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {row.company || ''}
                      </Typography>
                      {row.Assettype && (
                        <Typography variant="caption" display="block" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          {row.Assettype}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      {parseFloat(row.total_amount_exposure_perc) != null
                        ? `${parseFloat(row.total_amount_exposure_perc).toFixed(2)}%`
                        : '—'}
                      {isUnknown && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Unknown holdings part
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      {fmtCurrency(row.total_amount_exposure)}
                      {isUnknown && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Unknown holdings part
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography variant="body2">
                        {row.tracker_exposure_amount ? fmtCurrency(row.tracker_exposure_amount) : '—'}
                      </Typography>
                      {trackerDetails.length > 0 && (
                        <Box sx={{ textAlign: 'right' }}>
                          {trackerDetails.map((detail, i) => (
                            <Typography key={i} variant="caption" display="block" color="text.secondary">
                              {detail}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {row.stock_exposure ? fmtCurrency(row.stock_exposure) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

// ─── Build pie data + per-group holdings (file drop) ────────────

const buildFileDropAnalysis = (exposures, yfData = {}, data = []) => {
  const totalExposure = exposures.reduce((s, e) => s + (parseFloat(e.total_amount_exposure) || 0), 0);
  if (totalExposure === 0) return { pies: {}, holdingsByGroup: {} };

  const buckets = { region: {}, base_currency: {}, country: {}, industry: {}, sector: {}, Assettype: {} };
  const holdingsByGroup = { region: {}, base_currency: {}, country: {}, industry: {}, sector: {}, Assettype: {} };

  for (const exp of exposures) {
    const val = parseFloat(exp.total_amount_exposure) || 0;
    if (val === 0) continue;

    const ticker = (exp.ticker || '').toUpperCase();
    const yf = yfData[ticker] || {};
    const companyMatch = data.find(d => (d.Ticker || '').toUpperCase() === ticker);

    const currency = yf.currency || companyMatch?.currency || 'Unknown';
    const industry = yf.industry || 'Unknown';
    const sector = yf.sector || 'Unknown';
    const yfQuote = yf.Quotetype || '';
    const rawAssetType = exp.Assettype || yfQuote || 'Unknown';
    const companyName = exp.company || companyMatch?.Company || '';
    const assetType = classifyAssetType(rawAssetType, companyName, sector);

    // DAS-2689: Region & Country show MARKET (securities) exposure only — real estate
    // and cash are excluded from these two dimensions (see buildHoldingsMap).
    const isNonMarket = isNonMarketAssetClass(assetType);

    const holding = {
      ticker: exp.ticker || '',
      company: companyName,
      value: val,
      pct: (val / totalExposure) * 100,
    };

    const assign = (dim, key) => {
      buckets[dim][key] = (buckets[dim][key] || 0) + val;
      if (!holdingsByGroup[dim][key]) holdingsByGroup[dim][key] = [];
      holdingsByGroup[dim][key].push({ ...holding });
    };

    // Same market-only treatment for dropped-file portfolios (see buildHoldingsMap).
    if (!isNonMarket) {
      assign('region', yf.region || 'Unknown');
      assign('country', normalizeCountry(yf.country));
      assign('industry', industry);
      assign('sector', sector);
    }
    assign('base_currency', currency);
    assign('Assettype', assetType);
  }

  const pies = {};
  for (const [key, map] of Object.entries(buckets)) {
    pies[key] = Object.entries(map)
      .map(([name, amount]) => ({ name, exposure_perc: (amount / totalExposure) * 100 }))
      .sort((a, b) => b.exposure_perc - a.exposure_perc);
  }

  for (const dim of Object.keys(holdingsByGroup)) {
    for (const group of Object.keys(holdingsByGroup[dim])) {
      holdingsByGroup[dim][group].sort((a, b) => b.value - a.value);
    }
  }

  return { pies, holdingsByGroup };
};

// ─── Build per-group holdings from exposures + yfData (Redux) ───

const buildHoldingsMap = (exposures, yfData = {}, data = []) => {
  const totalExposure = exposures.reduce((s, e) => s + (parseFloat(e.total_amount_exposure) || 0), 0);
  if (totalExposure === 0) return {};

  const holdingsByGroup = { region: {}, base_currency: {}, country: {}, industry: {}, sector: {}, Assettype: {} };

  for (const exp of exposures) {
    const val = parseFloat(exp.total_amount_exposure) || 0;
    if (val === 0) continue;

    const ticker = (exp.ticker || '').toUpperCase();
    const yf = yfData[ticker] || {};
    const companyMatch = data.find(d => (d.Ticker || '').toUpperCase() === ticker);

    // DAS-2689: group every Exposures drill-down by the holding's own
    // Portfolio_Exposure fields — the SAME columns the pies are built from — so each
    // pie and its click-through list can never disagree. Previously these read yfData
    // (YF_Data.*), which for the synthetic W&O fund tickers is a stale 'USA'
    // region/country and listed EUR/Asia funds (correct in the pie) under USA.
    const currency = exp.base_currency || 'Unknown';
    const industry = exp.industry || 'Unknown';
    const sector = exp.sector || 'Unknown';
    const yfQuote = yf.Quotetype || '';
    const rawAssetType = exp.Assettype || yfQuote || 'Unknown';
    const companyName = exp.company || companyMatch?.Company || '';
    const assetType = classifyAssetType(rawAssetType, companyName, sector);

    // DAS-2689: Region & Country show MARKET (securities) exposure only. Real estate
    // and cash are excluded from these two dimensions — they are not a geographic
    // market exposure and, being large + EUR, they otherwise bury the actual regions.
    // They remain fully visible on the Asset Type + Breakdown tabs.
    const isNonMarket = isNonMarketAssetClass(assetType);

    const holding = {
      ticker: exp.ticker || '',
      company: companyName,
      value: val,
      pct: (val / totalExposure) * 100,
    };

    const assign = (dim, key) => {
      if (!holdingsByGroup[dim][key]) holdingsByGroup[dim][key] = [];
      holdingsByGroup[dim][key].push({ ...holding });
    };

    // Region/Country/Industry/Sector are MARKET dimensions — real estate & cash are
    // excluded (they have no equity sector/industry and are not a geographic exposure).
    if (!isNonMarket) {
      assign('region', exp.region || 'Unknown');
      assign('country', normalizeCountry(exp.country));
      assign('industry', industry);
      assign('sector', sector);
    }
    assign('base_currency', currency); // currency keeps everything (cash & real estate are real EUR)
    assign('Assettype', assetType);
  }

  for (const dim of Object.keys(holdingsByGroup)) {
    for (const group of Object.keys(holdingsByGroup[dim])) {
      holdingsByGroup[dim][group].sort((a, b) => b.value - a.value);
    }
  }

  return holdingsByGroup;
};

// ─── Detail dialog (table of holdings for one pie segment) ──────

const ExposureDetailDialog = ({ open, onClose, groupName, dimensionLabel, holdings = [], denomTotal = null, denomLabel = 'portfolio' }) => {
  const [orderBy, setOrderBy] = useState('value');
  const [orderDir, setOrderDir] = useState('desc');

  const handleSort = useCallback((field) => {
    setOrderDir(prev => (orderBy === field && prev === 'desc') ? 'asc' : 'desc');
    setOrderBy(field);
  }, [orderBy]);

  const sorted = useMemo(() => {
    const copy = [...holdings];
    const dir = orderDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      if (orderBy === 'ticker' || orderBy === 'company')
        return dir * (a[orderBy] || '').localeCompare(b[orderBy] || '');
      return dir * ((a[orderBy] || 0) - (b[orderBy] || 0));
    });
    return copy;
  }, [holdings, orderBy, orderDir]);

  const groupTotal = holdings.reduce((s, h) => s + h.value, 0);
  // DAS-2689: for Region/Country the pie is renormalised to securities, so show the
  // slice's share of that same denominator (denomTotal) to match the pie; other tabs
  // keep the plain % of total portfolio.
  const groupPct = denomTotal ? (groupTotal / denomTotal) * 100 : holdings.reduce((s, h) => s + h.pct, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        {groupName}
        <Chip label={`${holdings.length} holding${holdings.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 0.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 4 }}>
          {fmtCurrency(groupTotal)} · {fmtPct(groupPct)} of {denomLabel}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                {[
                  { id: 'company', label: 'Name', align: 'left' },
                  { id: 'ticker', label: 'Ticker', align: 'left' },
                  { id: 'value', label: 'Value (EUR)', align: 'right' },
                  { id: 'pct', label: '% of Portfolio', align: 'right' },
                ].map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDir : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((h, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell>{h.company || '—'}</TableCell>
                  <TableCell>
                    {h.ticker
                      ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{h.ticker}</Typography>
                      : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{fmtCurrency(h.value)}</TableCell>
                  <TableCell align="right">{fmtPct(h.pct)}</TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>No holdings in this group</TableCell>
                </TableRow>
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main exposures modal with tabs ─────────────────────────────

const PAModal_Exposures = ({ exposures, source = 'portfolio', yfData = {}, data = [], pdfMode = false, userId, onCreateJiraTicket }) => {
  const [tab, setTab] = useState(0);
  const [pieData, setPieData] = useState(null);
  const [pieLoading, setPieLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (source === 'file_drop') return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const userId = getUserId();
    if (!userId) return;

    setPieLoading(true);
    api.get('/pf_other_exposures', { params: { userid: userId } })
      .then(res => setPieData(res.data.portfolio_various_exposures_filtered))
      .catch(err => console.error('[PAModal_Exposures] Failed to fetch exposure pies:', err))
      .finally(() => setPieLoading(false));
  }, [source]);

  const fileDropAnalysis = useMemo(() => {
    if (source !== 'file_drop') return null;
    return buildFileDropAnalysis(exposures, yfData, data);
  }, [source, exposures, yfData, data]);

  const reduxHoldingsMap = useMemo(() => {
    if (source === 'file_drop') return null;
    return buildHoldingsMap(exposures, yfData, data);
  }, [source, exposures, yfData, data]);

  const activePieData = source === 'file_drop' ? fileDropAnalysis?.pies : pieData;
  const activeHoldingsMap = source === 'file_drop' ? (fileDropAnalysis?.holdingsByGroup || {}) : (reduxHoldingsMap || {});

  const activeTab = EXPOSURE_TABS[tab]?.key;

  const renderPie = (tabKey, title) => {
    const dataKey = PIE_DIM_KEYS[tabKey];
    if (pieLoading && source !== 'file_drop') {
      return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    }

    let items = activePieData?.[dataKey];

    // DAS-2689: derive Region/Country/Asset-Type pies from the same holdings map the
    // drill-down uses, so pie and click-through list are one source of truth. Percentages
    // are renormalised within the dimension: because Region & Country exclude real estate
    // and cash, their slices show the split OF YOUR SECURITIES (e.g. Europe vs USA),
    // summing to 100% — instead of the geographic split being dwarfed by the house/cash.
    if (HOLDINGS_DERIVED_PIES.includes(tabKey)) {
      const hm = activeHoldingsMap[dataKey] || {};
      const groups = Object.entries(hm).map(([name, holdings]) => ({
        name,
        val: holdings.reduce((sum, h) => sum + (h.value || 0), 0),
      }));
      const dimTotal = groups.reduce((sum, g) => sum + g.val, 0) || 1;
      const derived = groups
        .map(g => ({ name: g.name, exposure_perc: (g.val / dimTotal) * 100 }))
        .sort((a, b) => b.exposure_perc - a.exposure_perc);
      if (derived.length > 0) items = derived;
    }

    if (!items || items.length === 0) {
      return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No data available</Typography>;
    }

    const sorted = [...items].sort((a, b) => b.exposure_perc - a.exposure_perc);
    let topItems, otherItems, hasOtherGroup;
    if (sorted.length > MAX_PIE_SLICES) {
      topItems = sorted.slice(0, MAX_PIE_SLICES);
      otherItems = sorted.slice(MAX_PIE_SLICES);
      hasOtherGroup = true;
    } else {
      topItems = sorted;
      otherItems = [];
      hasOtherGroup = false;
    }
    const otherTotal = otherItems.reduce((s, i) => s + i.exposure_perc, 0);

    const chartData = [
      ...topItems.map((item, idx) => ({
        id: idx,
        value: item.exposure_perc,
        label: `${item.name} (${item.exposure_perc.toFixed(1)}%)`,
        color: PIE_COLORS[idx % PIE_COLORS.length],
      })),
      ...(hasOtherGroup ? [{
        id: 'other',
        value: otherTotal,
        label: `Other (${otherItems.length})`,
        color: OTHER_SLICE_COLOR,
      }] : []),
    ];

    const chartGroupNames = [...topItems.map(i => i.name), ...(hasOtherGroup ? ['__other__'] : [])];

    const holdingsForDim = activeHoldingsMap[dataKey] || {};
    const findHoldings = (name) => {
      if (!name) return [];
      return holdingsForDim[name] || holdingsForDim[name.toLowerCase()] ||
             holdingsForDim[name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()] || [];
    };

    const handleClick = (_event, itemIdentifier) => {
      const idx = itemIdentifier?.dataIndex;
      if (idx == null) return;
      const name = chartGroupNames[idx];
      if (name === '__other__') {
        setOtherExpanded(prev => !prev);
        return;
      }
      if (name && findHoldings(name).length > 0) {
        setDetailOpen({ dataKey, groupName: name, dimensionLabel: title });
      }
    };

    const tooltipFormatter = (item, context) => {
      const name = chartGroupNames[context?.dataIndex];
      if (!name || name === '__other__') return `${item.value.toFixed(2)}%`;
      const holdings = findHoldings(name);
      const top = holdings.slice(0, 5);
      const lines = top.map(h => `  ${h.ticker || h.company}: ${fmtPct(h.pct)}`);
      if (holdings.length > 5) lines.push(`  + ${holdings.length - 5} more — click to view all`);
      return [`${fmtPct(item.value)}`, ...lines].join('\n');
    };

    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', mt: 2 }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="flex-start">
          <Box flex="0 0 auto">
            <PieChart
              series={[{
                data: chartData,
                highlightScope: { fade: 'global', highlight: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                valueFormatter: tooltipFormatter,
                arcLabel: (item) => item.value > 20 ? item.label : '',
                arcLabelMinAngle: 0,
                arcLabelStyle: { fontSize: 14, fontWeight: 600, fill: '#000' },
              }]}
              width={400}
              height={350}
              slotProps={{ legend: { hidden: true } }}
              onItemClick={handleClick}
            />
          </Box>

          <Box flex="1 1 180px" sx={{ pt: 1, maxHeight: 390, overflowY: 'auto' }}>
            {chartData.map((item, i) => {
              const isOther = item.id === 'other';
              const itemName = isOther ? null : chartGroupNames[i];
              const hasHoldings = itemName && findHoldings(itemName).length > 0;
              return (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    py: 0.4, px: 0.5, borderRadius: 1,
                    cursor: (isOther || hasHoldings) ? 'pointer' : 'default',
                    '&:hover': (isOther || hasHoldings) ? { bgcolor: 'action.hover' } : undefined,
                  }}
                  onClick={() => {
                    if (isOther) setOtherExpanded(prev => !prev);
                    else if (hasHoldings) setDetailOpen({ dataKey, groupName: itemName, dimensionLabel: title });
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: 12 }} noWrap>
                    {isOther ? `Other (${otherItems.length})` : itemName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {item.value.toFixed(1)}%
                  </Typography>
                  {isOther && (
                    <IconButton size="small" sx={{ p: 0, ml: -0.5 }}>
                      {otherExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  )}
                </Box>
              );
            })}

            {hasOtherGroup && (
              <Collapse in={otherExpanded}>
                <Box sx={{ pl: 2.5, borderLeft: '2px solid', borderColor: OTHER_SLICE_COLOR, ml: 0.6, mt: 0.5 }}>
                  {otherItems.map((item) => {
                    const hasH = findHoldings(item.name).length > 0;
                    return (
                      <Box
                        key={item.name}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          py: 0.25, borderRadius: 1,
                          cursor: hasH ? 'pointer' : 'default',
                          '&:hover': hasH ? { bgcolor: 'action.hover' } : undefined,
                        }}
                        onClick={() => hasH && setDetailOpen({ dataKey, groupName: item.name, dimensionLabel: title })}
                      >
                        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', minWidth: 0, fontSize: 11 }} noWrap>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap', fontSize: 11 }}>
                          {item.exposure_perc.toFixed(1)}%
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </Box>
        </Box>

        {Object.keys(holdingsForDim).length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Click a segment or legend item to view holdings
          </Typography>
        )}
      </Box>
    );
  };

  const concentration = useMemo(() => computeConcentration(exposures), [exposures]);

  const detailHoldings = useMemo(() => {
    if (!detailOpen) return [];
    const dim = activeHoldingsMap[detailOpen.dataKey] || {};
    const name = detailOpen.groupName;
    return dim[name] || dim[name?.toLowerCase()] ||
           dim[name?.charAt(0).toUpperCase() + name?.slice(1).toLowerCase()] || [];
  }, [detailOpen, activeHoldingsMap]);

  // DAS-2689: Region & Country pies are renormalised to the securities in that dimension.
  // Give the drill-down the same denominator so its header % matches the pie slice.
  const detailDenom = useMemo(() => {
    if (!detailOpen || !SECURITIES_ONLY_DIMS.has(detailOpen.dataKey)) return null;
    const dim = activeHoldingsMap[detailOpen.dataKey] || {};
    const total = Object.values(dim).reduce(
      (s, hs) => s + hs.reduce((a, h) => a + (h.value || 0), 0), 0);
    return total || null;
  }, [detailOpen, activeHoldingsMap]);

  const jiraBtn = (tabLabel) => userId === 2 && onCreateJiraTicket ? (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
      <Tooltip title={`Create Jira Ticket – ${tabLabel}`}>
        <IconButton size="small" onClick={() => onCreateJiraTicket(`Exposures – ${tabLabel}`)}>
          <ConfirmationNumberIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <Box>
      <ConcentrationPanel metrics={concentration} />

      {!pdfMode && (
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setOtherExpanded(false); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {EXPOSURE_TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      )}

      {(pdfMode || activeTab === 'breakdown') && (
        <Box data-pdf-section="Breakdown" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Breakdown</Typography>}
          {jiraBtn('Breakdown')}
          <ExposureBreakdownTable exposures={exposures} source={source} />
        </Box>
      )}
      {(pdfMode || activeTab === 'region') && (
        <Box data-pdf-section="Region" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Region</Typography>}
          {jiraBtn('Region')}
          {renderPie('region', 'Region Exposure (securities)')}
        </Box>
      )}
      {(pdfMode || activeTab === 'currency') && (
        <Box data-pdf-section="Currency" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Currency</Typography>}
          {jiraBtn('Currency')}
          {renderPie('currency', 'Currency Exposure')}
        </Box>
      )}
      {(pdfMode || activeTab === 'country') && (
        <Box data-pdf-section="Country" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Country</Typography>}
          {jiraBtn('Country')}
          {renderPie('country', 'Country Exposure (securities)')}
        </Box>
      )}
      {(pdfMode || activeTab === 'industry') && (
        <Box data-pdf-section="Industry" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Industry</Typography>}
          {jiraBtn('Industry')}
          {renderPie('industry', 'Industry Exposure (securities)')}
        </Box>
      )}
      {(pdfMode || activeTab === 'sector') && (
        <Box data-pdf-section="Sector" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Sector</Typography>}
          {jiraBtn('Sector')}
          {renderPie('sector', 'Sector Exposure (securities)')}
        </Box>
      )}
      {(pdfMode || activeTab === 'assettype') && (
        <Box data-pdf-section="Asset Type" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Asset Type</Typography>}
          {jiraBtn('Asset Type')}
          {renderPie('assettype', 'Asset Type Exposure')}
        </Box>
      )}

      <ExposureDetailDialog
        open={!!detailOpen}
        onClose={() => setDetailOpen(null)}
        groupName={detailOpen?.groupName || ''}
        dimensionLabel={detailOpen?.dimensionLabel || ''}
        holdings={detailHoldings}
        denomTotal={detailDenom}
        denomLabel={detailDenom ? 'securities' : 'portfolio'}
      />
    </Box>
  );
};

export default PAModal_Exposures;
